import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import * as bulkApi from '@/api/bulk';
import { designKeys } from './useDesigns';
import { reviewKeys } from './useReviewQueue';

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════

export const bulkKeys = {
  all: ['bulk'] as const,
  references: () => [...bulkKeys.all, 'references'] as const,
  referencePages: (query: BulkReferencesFilter) =>
    [...bulkKeys.references(), 'pages', query] as const,
  jobs: () => [...bulkKeys.all, 'jobs'] as const,
  job: (jobId: string | null) => [...bulkKeys.jobs(), 'detail', jobId] as const,
};

/** Search, window and sort of the reference list; paging is the hook's. */
export type BulkReferencesFilter = Omit<
  bulkApi.BulkReferencesQuery,
  'page' | 'limit'
>;

/** References per "Mehr laden" page in step 01. */
export const BULK_REFERENCES_PAGE_SIZE = 20;

/** Poll interval of the progress card while a job is queued or running. */
export const BULK_JOBS_POLL_INTERVAL_MS = 3000;

/** Report page size per request (server cap per page). */
const JOB_REPORT_PAGE_SIZE = 50;

/**
 * Defensive bound on cursor-following: a run has at most 100 rows (2
 * pages), so a server-side cursor bug can never spin an endless loop.
 */
const MAX_JOB_REPORT_PAGES = 10;

function isActiveStatus(status: bulkApi.BulkJobStatus | undefined): boolean {
  return status === 'QUEUED' || status === 'RUNNING';
}

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Reference listings for step 01 as a paged list ("Mehr laden"), like
 * `useInfiniteDesigns`. Keeps the previous result on screen while a new
 * search, window or sort order loads (`isPlaceholderData`).
 */
export function useInfiniteBulkReferences(filter: BulkReferencesFilter) {
  return useInfiniteQuery({
    queryKey: bulkKeys.referencePages(filter),
    queryFn: ({ pageParam }) =>
      bulkApi.getBulkReferences({
        ...filter,
        page: pageParam,
        limit: BULK_REFERENCES_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
    placeholderData: keepPreviousData,
  });
}

/**
 * The seller's bulk jobs that were not dismissed (progress card). Polls
 * every 3 s while any job is queued or running and stops once all of them
 * reached a terminal state.
 */
export function useBulkJobs() {
  return useQuery({
    queryKey: bulkKeys.jobs(),
    queryFn: bulkApi.getBulkJobs,
    refetchInterval: (query) =>
      query.state.data?.some((job) => isActiveStatus(job.status))
        ? BULK_JOBS_POLL_INTERVAL_MS
        : false,
  });
}

/**
 * One bulk job with its report. While the job is active only the first page
 * is fetched (the next poll refreshes it anyway); once terminal the
 * remaining pages are followed via `nextCursor`, like `useReviewJob`.
 */
export function useBulkJob(jobId: string | null) {
  return useQuery({
    queryKey: bulkKeys.job(jobId),
    queryFn: async (): Promise<bulkApi.BulkJobDetail> => {
      const id = jobId as string;
      const first = await bulkApi.getBulkJob(
        id,
        undefined,
        JOB_REPORT_PAGE_SIZE
      );
      if (isActiveStatus(first.job.status)) {
        return first;
      }
      let page = first;
      const items = [...first.items];
      let fetched = 1;
      while (page.nextCursor !== null && fetched < MAX_JOB_REPORT_PAGES) {
        page = await bulkApi.getBulkJob(
          id,
          page.nextCursor,
          JOB_REPORT_PAGE_SIZE
        );
        items.push(...page.items);
        fetched += 1;
      }
      return { ...first, items, nextCursor: page.nextCursor };
    },
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;
      return status === undefined || isActiveStatus(status)
        ? BULK_JOBS_POLL_INTERVAL_MS
        : false;
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Starts a bulk run. A new job changes the design overview, the review
 * queue (draft mode) and the progress card, so all three refresh.
 */
export function useCreateBulkJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: bulkApi.CreateBulkJobInput) =>
      bulkApi.createBulkJob(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: designKeys.lists() });
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      queryClient.invalidateQueries({ queryKey: bulkKeys.jobs() });
    },
  });
}

/** Hides a finished job from the progress card. */
export function useDismissBulkJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => bulkApi.dismissBulkJob(jobId),
    onSuccess: ({ id }) => {
      // Drop the card at once instead of waiting for the refetch.
      queryClient.setQueryData<bulkApi.BulkJobListItem[]>(
        bulkKeys.jobs(),
        (jobs) => jobs?.filter((job) => job.id !== id)
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bulkKeys.jobs() });
    },
  });
}
